/**
 * ToastShell
 *
 * Everything a toast is apart from what it says: the surface, the enter and
 * exit, the clock, the dismiss button, the role, the leading rail and the
 * hairline along the foot that drains as the time left. Content is the
 * caller's — a toast drawn through the provider's `render` hook composes this
 * shell and brings only its own copy and buttons.
 *
 * The surface is the popover spec from colour-system.md — base tone, default
 * stroke, lift-popover shadow — rather than a lighter card: a toast floats,
 * and floating is scrim-and-shadow's job, not the paint's.
 *
 * The clock stops while the pointer or focus is on the toast, or while the
 * caller holds it (`paused`), and picks up with the time that was left — so a
 * question is never whisked away mid-read.
 */

import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IconButton } from '../Button';

import type { ToastShellProps, ToastShellVariant } from './Toast.types';
import type { FocusEvent } from 'react';

/** Matches the exit transition (`duration-normal`). */
const EXIT_MS = 200;

/**
 * The leading rail. The semantic variants carry their state; `accent` is the
 * inbox's unread rail, asked for by name; `default` has no state to report,
 * so it draws none.
 */
const RAIL_CLASSES: Record<ToastShellVariant, string | null> = {
  default: null,
  accent: 'bg-primary-400',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
};

export function ToastShell({
  variant = 'default',
  duration,
  paused = false,
  dismissible = true,
  onDismiss,
  onRemove,
  children,
  'data-testid': testId,
}: ToastShellProps): React.JSX.Element {
  const [entered, setEntered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  // Time left, kept across pauses so resuming does not restart the clock.
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(0);

  // Once: a second dismiss during the exit — or one that lands after the
  // toast has gone — must not remove it twice.
  const exitTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const isHeld = paused || hovered || focused || leaving;

  const dismiss = useCallback(() => {
    if (!mountedRef.current || exitTimerRef.current !== null) return;

    onDismiss?.();
    setLeaving(true);
    exitTimerRef.current = window.setTimeout(onRemove, EXIT_MS);
  }, [onDismiss, onRemove]);

  useEffect(() => {
    // Re-armed on mount: StrictMode runs this cleanup once before the real one.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (exitTimerRef.current !== null) {
        window.clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (duration === 0 || isHeld) return;

    startedAtRef.current = performance.now();
    const timer = window.setTimeout(dismiss, remainingRef.current);

    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (performance.now() - startedAtRef.current)
      );
    };
  }, [duration, isHeld, dismiss]);

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    // Focus moving between the toast's own buttons has not left the toast.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setFocused(false);
  };

  // A failure interrupts; everything else waits its turn.
  const isUrgent = variant === 'error';
  const isShown = entered && !leaving;
  const rail = RAIL_CLASSES[variant];

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-testid={testId}
      data-variant={variant}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      className={[
        'relative w-90 max-w-[calc(100vw-2rem)] overflow-hidden',
        'rounded-lg border border-stroke-default bg-contrast-950 shadow-lift-popover',
        'transform transition-all duration-normal',
        isShown
          ? 'translate-x-0 translate-y-0 opacity-100 ease-out'
          : '-translate-y-2 opacity-0 ease-in sm:translate-x-3 sm:translate-y-0',
      ].join(' ')}
    >
      {rail && (
        <span
          aria-hidden="true"
          className={`absolute bottom-3 left-0 top-3 w-0.5 rounded-full ${rail}`}
        />
      )}

      <div
        className={`flex gap-3 py-3.5 pl-4 ${dismissible ? 'pr-3' : 'pr-4'}`}
      >
        <div className="flex min-w-0 flex-1 gap-3">
          {typeof children === 'function' ? children({ dismiss }) : children}
        </div>

        {/* Muted, so it never competes with the message. The equal pull above
            and below keeps its 32px box out of the row's height: the X centres
            on the first line, and a one-line toast stays centred. */}
        {dismissible && (
          <IconButton
            variant="ghost-muted"
            size="sm"
            icon={<XMarkIcon />}
            aria-label="Dismiss notification"
            onClick={dismiss}
            className="-mr-1.5 -my-1.5 shrink-0"
          />
        )}
      </div>

      {duration > 0 && (
        <span
          aria-hidden="true"
          data-testid={testId ? `${testId}-timer` : undefined}
          className="animate-toast-drain absolute inset-x-0 bottom-0 h-px bg-stroke-strong"
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: isHeld ? 'paused' : 'running',
          }}
        />
      )}
    </div>
  );
}

export default ToastShell;
